// RosRail — the persistent left navigation rail (lives in AppShell across all routes). Reads
// cross-route state from RosShellContext.
//
// Destinations (Projects, Artifacts, Extensions, Settings), optional Pinned Extensions (localStorage),
// then Recent chats. Collapses to icons-only with hover tooltips. Account block at the bottom.

import { useEffect, useRef, useState } from 'react'
import { keyframes } from '@mui/system'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import Popper from '@mui/material/Popper'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import { useTheme } from '@mui/material/styles'
import { useLocation, useNavigate } from 'react-router-dom'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import HomeRoundedIcon from '@mui/icons-material/HomeRounded'
import ChatIcon from '@mui/icons-material/Chat'
import FolderIcon from '@mui/icons-material/Folder'
import WidgetsIcon from '@mui/icons-material/Widgets'
import ExtensionIcon from '@mui/icons-material/Extension'
import SettingsIcon from '@mui/icons-material/Settings'
import GridViewIcon from '@mui/icons-material/GridView'
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined'
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined'
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize'
import GroupsIcon from '@mui/icons-material/Groups'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CheckIcon from '@mui/icons-material/Check'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import LogoutIcon from '@mui/icons-material/Logout'
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import ChatBubbleIcon from '@mui/icons-material/ChatBubble'
import BuildIcon from '@mui/icons-material/Build'
import PublicIcon from '@mui/icons-material/Public'
import AddIcon from '@mui/icons-material/Add'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import PushPinIcon from '@mui/icons-material/PushPin'
import type { SessionSummary } from '@reachinsight-app/shared/src/components/R3Chat/types'
import StatusBadge from '@reachinsight-app/shared/src/components/StatusBadge'
import { ArtifactIndicator } from './ArtifactIndicator'
import { ResolvedAvatar } from './SettingsGeneral'
import AIBadge from '@reachinsight-app/shared/src/components/AIBadge'
import { useRosShell } from '../RosShellContext'
import { config } from '../config/environment'
import { resolveIcon } from '../config/registryIcons'
import { SERIF_HEADING } from '../theme'
import { NotificationsMenu } from './NotificationsMenu'
import {
  resolveExtensionUrl,
  unpinExtension,
  usePinnedExtensions,
  type PinnedExtension,
} from '../pinnedExtensions'
import { listRosCatalog, type RosCatalogEntry } from '../pages/registryCatalogApi'
import { useRosFeatures } from '../access/useRosFeatures'
import { useFeatureFlag } from '@reachinsight-app/shared/src'

const EXPANDED = 248
const COLLAPSED = 64

// Collapse/expand motion. One shared easing (MUI's standard decelerate curve) + duration so the rail
// width, the padding shifts, and the label/mark reveals all move on the same clock — the difference
// between a smooth single motion and content popping in before the rail finishes sliding.
const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)'
const MOTION = '0.24s'
// Used for blocks that only exist in ONE mode (recent list, account text, the swapped new-chat
// buttons): they can't cross-fade a persistent element, so they fade in on mount instead.
const fadeIn = keyframes({ from: { opacity: 0 }, to: { opacity: 1 } })
type Mode = 'chat' | 'workflows'
type BadgeStatus = 'new' | 'alpha' | 'beta' | 'community' | 'coming-soon'

function relativeActivity(ts: number): string {
  const elapsed = Math.max(0, Date.now() - ts)
  const minutes = Math.floor(elapsed / 60_000)
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return new Date(ts).toLocaleDateString(undefined, {month: 'short', day: 'numeric'})
}

function RecentChatRow({session, active, artifactCount, onOpen, onPin}: {
  session: SessionSummary
  active: boolean
  artifactCount: number | undefined
  onOpen: () => void
  onPin: (pinned: boolean) => void
}) {
  const [hoverAnchor, setHoverAnchor] = useState<HTMLElement | null>(null)
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastActive = session.lastActiveAt ?? session.startedAt

  const cancelHover = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    hoverTimer.current = null
    setHoverAnchor(null)
  }
  useEffect(() => () => { if (hoverTimer.current) clearTimeout(hoverTimer.current) }, [])

  return (
    <>
      <Box
        onMouseEnter={event => {
          const anchor = event.currentTarget
          if (hoverTimer.current) clearTimeout(hoverTimer.current)
          hoverTimer.current = setTimeout(() => setHoverAnchor(anchor), 450)
        }}
        onMouseLeave={cancelHover}
        onClick={onOpen}
        sx={{
          mx: 1, px: 1.25, py: 0.6, borderRadius: 1.5, cursor: 'pointer',
          color: 'text.primary',
          bgcolor: active ? 'rgba(0, 143, 168, 0.10)' : 'transparent',
          '&:hover': { bgcolor: active ? 'rgba(0, 143, 168, 0.14)' : 'action.hover' },
          '&:hover .chat-pin': { opacity: 1 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Typography noWrap sx={{ flex: 1, fontSize: '14.5px', fontWeight: 500 }}>
            {session.intentSummary || 'Untitled chat'}
          </Typography>
          <ArtifactIndicator count={artifactCount} />
          <IconButton
            className="chat-pin"
            size="small"
            aria-label={session.pinnedAt ? 'Unpin chat' : 'Pin chat'}
            onClick={event => {
              event.stopPropagation()
              cancelHover()
              onPin(!session.pinnedAt)
            }}
            sx={{
              width: 24, height: 24, p: 0.25, flexShrink: 0,
              opacity: session.pinnedAt ? 1 : {xs: 1, md: 0},
              color: session.pinnedAt ? 'primary.main' : 'text.secondary',
              transition: 'opacity 0.15s, color 0.15s',
            }}
          >
            {session.pinnedAt ? <PushPinIcon sx={{fontSize: 16}} /> : <PushPinOutlinedIcon sx={{fontSize: 16}} />}
          </IconButton>
        </Box>
      </Box>
      <Popper
        open={Boolean(hoverAnchor)} anchorEl={hoverAnchor} placement="right-start"
        modifiers={[{name: 'offset', options: {offset: [0, 10]}}]}
        sx={{zIndex: theme => theme.zIndex.tooltip, pointerEvents: 'none'}}
      >
        <Paper elevation={5} sx={{width: 310, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', p: 1.5}}>
          <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1.25}}>
            <Typography sx={{flex: 1, fontSize: '0.95rem', fontWeight: 650, lineHeight: 1.3}}>
              {session.intentSummary || 'Untitled chat'}
            </Typography>
            <Typography sx={{fontSize: '0.78rem', color: 'text.secondary', whiteSpace: 'nowrap', pt: 0.15}}>
              {relativeActivity(lastActive)}
            </Typography>
          </Box>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.2, color: 'text.secondary'}}>
            <FolderOutlinedIcon sx={{fontSize: 18}} />
            <Typography noWrap sx={{fontSize: '0.84rem'}}>{session.projectName || 'Unassigned'}</Typography>
          </Box>
          {Boolean(artifactCount) && (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.8, color: 'text.secondary'}}>
              <WidgetsIcon sx={{fontSize: 18}} />
              <Typography sx={{fontSize: '0.84rem'}}>
                {artifactCount} artifact{artifactCount === 1 ? '' : 's'}
              </Typography>
            </Box>
          )}
          <Typography sx={{fontSize: '0.72rem', color: 'text.disabled', mt: 0.8}}>
            Last active {new Date(lastActive).toLocaleString()}
          </Typography>
        </Paper>
      </Popper>
    </>
  )
}

// The two top-level tabs. 'chat' → "Ros" (chat-bubble); 'workflows' → "Tools" (wrench). The Ros icon
// is FILLED when its tab is active, outline when not (Tools' wrench has no outline variant, reused).
interface TabDef { mode: Mode; label: string; icon: React.ReactNode; activeIcon: React.ReactNode }
const TABS: TabDef[] = [
  { mode: 'chat', label: 'Ros', icon: <ChatBubbleOutlineIcon fontSize="small" />, activeIcon: <ChatBubbleIcon fontSize="small" /> },
  { mode: 'workflows', label: 'Tools', icon: <BuildIcon fontSize="small" />, activeIcon: <BuildIcon fontSize="small" /> },
]

interface NavDest {
  label: string
  icon: React.ReactNode
  path: string
  badge?: BadgeStatus
  /** Tooltip override (shown when the rail is collapsed). Defaults to the label. */
  tip?: string
}

// The user's active org / research domain. Static for now (no org membership in the token yet); shown
// as a single ✓ row. Structured this way so multiple research domains can be listed here later.
const ACTIVE_ORG = 'Rival Technologies'

// Account-menu language options. English is the only active locale today; the rest preview the future
// (the submenu lists them but only English is selectable) so the capability is visible, not a dead control.
const LANGUAGES = [
  'English (United States)', 'Français (France)', 'Deutsch (Deutschland)', 'हिन्दी (भारत)',
  'Italiano (Italia)', '日本語 (日本)', '한국어 (대한민국)', 'Português (Brasil)', 'Español (España)',
]

// "Ask Ros" is gone (replaced by the + New chat button); "Chats" is gone from the list too — recent
// chats + the "All chats" link cover it in expanded mode, and a dedicated collapsed affordance covers
// it when collapsed. What remains is the destination list from the mockup.
const PROJECTS_DEST: NavDest = { label: 'Projects', icon: <FolderIcon fontSize="small" />, path: '/projects' }
const ARTIFACTS_DEST: NavDest = { label: 'Artifacts', icon: <WidgetsIcon fontSize="small" />, path: '/artifacts' }
const SKILLS_DEST: NavDest = { label: 'Skills', icon: <ExtensionIcon fontSize="small" />, path: '/skills' }
const EXTENSIONS_DEST: NavDest = { label: 'Extensions', icon: <GridViewIcon fontSize="small" />, path: '/extensions', tip: 'Find extensions; Open docks them beside Ros chat' }
const SETTINGS_DEST: NavDest = { label: 'Settings', icon: <SettingsIcon fontSize="small" />, path: '/settings' }

export interface RosRailProps {
  apiBaseUrl: string
  token: string | null
  onOpenArtifactVersion: (v: { id: string; type: string; version: number; title: string; sessionId: string; bodyR2Key: string }) => void
}

export function RosRail(_props: RosRailProps) {
  const shell = useRosShell()
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useTheme()
  const { enabled, authFetch } = useRosFeatures()
  const trackBg = theme.palette.mode === 'dark' ? theme.palette.background.default : '#f5f3ec'
  const collapsed = shell.sidebarCollapsed
  const [mode, setMode] = useState<Mode>('chat')
  const showExtensions = useFeatureFlag('ros-extensions')
  const showSkills = enabled('ros-skills')
  const showTools = enabled('ros-tools')
  const visibleTabs = showTools ? TABS : TABS.filter(tab => tab.mode === 'chat')
  const pinned = usePinnedExtensions()
  const [acctAnchor, setAcctAnchor] = useState<null | HTMLElement>(null)
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null)
  const [tabPopOpen, setTabPopOpen] = useState(false)

  const dests: NavDest[] = [
    PROJECTS_DEST,
    ARTIFACTS_DEST,
    showExtensions ? EXTENSIONS_DEST : SKILLS_DEST,
    SETTINGS_DEST,
  ].filter(d => d.path !== '/skills' || showSkills)

  // App/tool registry, surfaced under the static tools when the Tools tab is open. Fetched once the
  // Tools tab is shown (and re-fetched if the token changes). Failure/empty is silent: the rail just
  // shows the static tools, never an error.
  const [registryEntries, setRegistryEntries] = useState<RosCatalogEntry[]>([])
  const [catalogError, setCatalogError] = useState(false)
  const [catalogAttempt, setCatalogAttempt] = useState(0)
  useEffect(() => {
    if (!showTools) {
      setMode('chat')
      setRegistryEntries([])
      setCatalogError(false)
      return
    }
    if (mode !== 'workflows') return
    let cancelled = false
    setCatalogError(false)
    listRosCatalog(shell.apiBaseUrl, authFetch)
      .then(entries => { if (!cancelled) setRegistryEntries(entries) })
      .catch(() => { if (!cancelled) { setRegistryEntries([]); setCatalogError(true) } })
    return () => { cancelled = true }
  }, [mode, showTools, shell.apiBaseUrl, authFetch, catalogAttempt])

  // "Ask Ros" (path '/chat') highlights only while you're on a BLANK chat — on a /chat screen with no
  // messages yet ("you're about to ask Ros"). The moment the conversation has content the highlight
  // drops (you're now in a specific chat, reflected by the Recent-chats highlight instead). Clicking
  // it still always starts a net-new chat. Every other row highlights on an exact path match
  // ('/chats' must NOT match '/chat').
  const onChat = location.pathname === '/chat' || location.pathname.startsWith('/chat/')
  const isActive = (path: string) => {
    if (path === '/chat') return onChat && !shell.chatHasMessages
    // Projects stays active on its detail route too (/projects/:id).
    if (path === '/projects') return location.pathname === '/projects' || location.pathname.startsWith('/projects/')
    return location.pathname === path
  }

  const recent = shell.sessions.slice(0, 10)
  // Registry launch. A registry url is EITHER an absolute url (a standalone app like Focus Group
  // Finder, or a public app) OR a relative path (a seeded Studio tool, e.g. /survey-analysis). For a
  // relative path we prepend Studio's per-environment origin (config.STUDIO_URL, the same value the
  // flag-off tools use), so a Studio tool opens on the correct host and rides the shared session.
  const openRegistryEntry = (e: RosCatalogEntry) => {
    const href = /^https?:\/\//i.test(e.url) ? e.url : `${config.STUDIO_URL}${e.url}`
    window.open(href, '_blank', 'noopener')
  }

  const flatRegistryEntries = registryEntries.filter(e => e.name.toLowerCase() !== 'skills')

  const openPinned = (item: PinnedExtension) => {
    if (item.kind === 'gadget' || item.capnWeb) {
      const defId = item.definitionId || item.id
      navigate(`/gadgets/${defId}`, {
        state: {
          displayName: item.name,
          kind: item.kind === 'app' ? 'app' : 'gadget',
          registryEntryId: item.id,
        },
      })
      return
    }
    if (item.kind === 'app') {
      navigate(`/apps/${item.id}`, {
        state: {
          name: item.name,
          url: item.url ? resolveExtensionUrl(item.url) : undefined,
          kind: 'app' as const,
        },
      })
      return
    }
    if (item.url) {
      window.open(resolveExtensionUrl(item.url), '_blank', 'noopener')
    } else {
      navigate('/extensions')
    }
  }

  const pinnedIcon = (item: PinnedExtension) => {
    if (item.kind === 'gadget') return <DashboardCustomizeIcon fontSize="small" />
    if (item.kind === 'app') return <GroupsIcon fontSize="small" />
    return <OpenInNewIcon fontSize="small" />
  }

  // A single nav/tool row (shared shape for destinations + tools).
  const Row = ({ icon, label, active, badge, ai, onClick, tip }: {
    icon: React.ReactNode; label: string; active?: boolean; badge?: BadgeStatus; ai?: boolean; onClick: () => void; tip: string
  }) => {
    const row = (
      <Box
        onClick={onClick}
        sx={{
          display: 'flex', alignItems: 'center',
          // The highlight runs EDGE-TO-EDGE (no side margin, square sides) in BOTH states like the
          // designer — padding keeps content off the edge. A clean soft cyan fill marks the active item.
          // Fixed row height so every nav item sits at the same Y in both collapsed and expanded modes
          // (otherwise an icon-only collapsed row is shorter than a labelled expanded row and they drift).
          // Left padding is CONSTANT (22px) across both modes, so the icon holds the exact same X while
          // the rail widens — the frame expands and the label reveals beside a static icon, no sideways
          // travel. 22px centers a 20px icon in the 64px collapsed rail.
          mx: 0, pl: '22px', pr: 2, py: 0.9, minHeight: 40,
          justifyContent: 'flex-start',
          borderRadius: 0, cursor: 'pointer',
          // Active = cyan-branded: cyan icon/text on a faint cyan tint.
          // Inactive = softer warm-stone grey, so the active item clearly pops as "selected".
          color: 'text.primary',
          bgcolor: active ? 'rgba(0, 143, 168, 0.10)' : 'transparent',
          fontWeight: 500,
          '&:hover': { bgcolor: active ? 'rgba(0, 143, 168, 0.14)' : 'action.hover' },
        }}
      >
        {icon}
        {/* Label stays mounted in both modes and reveals via max-width + opacity, so it widens in step
            with the rail instead of popping in fully-formed while the rail is still narrow. */}
        <Typography sx={{
          fontSize: '14.5px', fontWeight: 'inherit', whiteSpace: 'nowrap', overflow: 'hidden',
          flexGrow: 1, flexShrink: 1,
          maxWidth: collapsed ? 0 : '160px', ml: collapsed ? 0 : 1.25, opacity: collapsed ? 0 : 1,
          transition: `max-width ${MOTION} ${EASE}, margin-left ${MOTION} ${EASE}, opacity 0.18s ease`,
        }}>{label}</Typography>
        {/* "Coming soon" reads as neutral/inactive in the rail — render it in warm Stone (like COMMUNITY)
            rather than the shared ochre, so it doesn't compete with the live cyan nav items. */}
        {!collapsed && badge === 'coming-soon' ? (
          <Box sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: '#57524a', color: 'white', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.04em' }}>SOON</Box>
        ) : !collapsed && badge ? (
          <StatusBadge status={badge} size="small" />
        ) : null}
        {!collapsed && ai && <AIBadge size="small" />}
      </Box>
    )
    return collapsed ? <Tooltip title={tip} placement="right">{row}</Tooltip> : row
  }

  return (
    <Box sx={{
      width: collapsed ? COLLAPSED : EXPANDED,
      minWidth: collapsed ? COLLAPSED : EXPANDED,
      height: '100vh',
      borderRight: '1px solid', borderColor: 'divider',
      bgcolor: 'background.paper',
      display: 'flex', flexDirection: 'column',
      transition: `width ${MOTION} ${EASE}, min-width ${MOTION} ${EASE}`,
      flexShrink: 0,
      // Lift the whole rail into its own stacking context above the content pane. The collapsed
      // tab flyout overflows the rail's right edge; without this the content pane (a position:
      // relative sibling in AppShell) paints over the flyout despite its z-index, clipping it to
      // just the icon. zIndex stays well below the app's 1400 overlay layer (dialogs/menus).
      position: 'relative',
      zIndex: 30,
    }}>
      {/* Brand lockup. No divider under the header (matches mock). The collapse chevron is a circular
          outlined button straddling the right edge of the rail. */}
      <Box sx={{
        position: 'relative',
        display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
        // Same trick as the nav rows: CONSTANT 20px left padding in both modes (centers the 24px home
        // icon in the 64px collapsed rail) so the brand mark holds its X — the wordmark reveals beside a
        // static home icon as the rail widens, nothing slides.
        // FIXED height (not minHeight): expanded shows a 36px "+" button that, with vertical padding,
        // used to stretch the header to 64px while collapsed stayed at 56 — an 8px jump that shoved the
        // whole nav up/down on every toggle. A hard 56px height + alignItems:center holds it identical in
        // both modes (the 36px button centers comfortably inside 56px, no padding needed).
        pl: '20px', pr: 2, height: 56,
      }}>
        {/* Brand / home button. Expanded: the "RivalOS" wordmark in Lora (no logo mark). Collapsed: a
            home icon. Both stay mounted and cross-fade (max-width + opacity) so the swap tracks the rail
            width instead of snapping between two different glyphs. */}
        <Box onClick={() => navigate('/')} sx={{ display: 'flex', alignItems: 'center', minWidth: 0, cursor: 'pointer' }}>
          <HomeRoundedIcon sx={{
            fontSize: 24, color: 'text.primary', overflow: 'hidden',
            maxWidth: collapsed ? '24px' : 0, opacity: collapsed ? 1 : 0,
            transition: `max-width ${MOTION} ${EASE}, opacity 0.18s ease`,
          }} />
          <Typography sx={{
            ...SERIF_HEADING, fontFamily: '"Lora", Georgia, serif', fontSize: '1.6rem', lineHeight: 1,
            color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden',
            maxWidth: collapsed ? 0 : '180px', opacity: collapsed ? 0 : 1,
            transition: `max-width ${MOTION} ${EASE}, opacity 0.18s ease`,
          }}>RivalOS</Typography>
        </Box>

        {/* New chat — the + button that replaces the old "Ask Ros" nav item. Expanded: sits at the
            right of the brand row (before the collapse chevron). Collapsed: the header centers the
            logo, so the + moves into the body (rendered below) per the mockup's position change. */}
        {!collapsed && (
          <Tooltip title="New chat" placement="bottom">
            <IconButton
              size="small" onClick={() => shell.newChat()}
              sx={{ ml: 'auto', mr: 1.5, width: 36, height: 36, p: 0, borderRadius: '14px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', color: 'primary.main', boxShadow: '0 1px 0 rgba(40,36,31,0.04)', animation: `${fadeIn} ${MOTION} ease both`, '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(0,143,168,0.10)' } }}
            >
              <AddIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Circular collapse/expand button — ALWAYS on the right edge of the rail, vertically centered
            with the lockup, straddling the border (same position collapsed or expanded; matches mock). */}
        <Tooltip title={collapsed ? 'Expand' : 'Collapse'} placement="right">
          <IconButton
            size="small"
            onClick={() => shell.setSidebarCollapsed(!collapsed)}
            sx={{
              position: 'absolute', right: -13, top: '50%', transform: 'translateY(-50%)',
              width: 26, height: 26,
              bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
              color: 'text.secondary', boxShadow: 1, zIndex: 2,
              '&:hover': { bgcolor: 'background.paper', borderColor: 'primary.main', color: 'primary.main' },
            }}
          >
            {collapsed ? <ChevronRightIcon sx={{ fontSize: 16 }} /> : <ChevronLeftIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {!showExtensions && showTools && (!collapsed ? (
        <Box sx={{ mt: '8px', mb: 0.5, height: 46, overflowX: 'hidden' }}>
          <Box sx={{
            width: `${EXPANDED - 32}px`, ml: '16px', height: 46, boxSizing: 'border-box',
            display: 'flex', flexDirection: 'row', gap: 0,
            bgcolor: trackBg, border: '1px solid', borderColor: 'divider', borderRadius: '14px', p: '3px',
            animation: `${fadeIn} ${MOTION} ease both`,
          }}>
          {visibleTabs.map(t => (
            <Box
              key={t.mode}
              onClick={() => setMode(t.mode)}
              sx={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                borderRadius: '10px', cursor: 'pointer', py: '9px', px: '10px',
                fontSize: '13.5px', fontWeight: 600,
                color: mode === t.mode ? 'primary.main' : 'text.primary',
                bgcolor: mode === t.mode ? 'background.paper' : 'transparent',
                boxShadow: mode === t.mode ? '0 1px 0 rgba(40,36,31,0.04)' : 'none',
                transition: 'color 0.2s',
                '&:hover': mode === t.mode ? {} : { color: 'primary.dark' },
              }}
            >
              {mode === t.mode ? t.activeIcon : t.icon}
              {t.label}
            </Box>
          ))}
          </Box>
        </Box>
      ) : (
        <Box sx={{ mt: '8px', mb: 0.5, height: 46, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', pl: '9px', animation: `${fadeIn} ${MOTION} ease both` }}>
          <Box
            onClick={() => setTabPopOpen(true)}
            sx={{
              width: 46, height: 46, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: '14px', cursor: 'pointer', color: 'primary.main',
              bgcolor: trackBg, border: '1px solid', borderColor: 'divider',
              transition: 'background .15s, border-color .15s',
              '&:hover': { bgcolor: 'background.paper', borderColor: 'primary.main' },
            }}
          >
            {visibleTabs.find(t => t.mode === mode)!.activeIcon}
          </Box>
          {tabPopOpen && (
            <>
              <Box onClick={() => setTabPopOpen(false)} sx={{ position: 'fixed', inset: 0, zIndex: 55 }} />
              <Box sx={{
                position: 'absolute', top: 0, left: '9px', zIndex: 60,
                display: 'flex', flexDirection: 'row', gap: 0, width: 184, minHeight: 46, boxSizing: 'border-box',
                bgcolor: trackBg, border: '1px solid', borderColor: 'primary.main', borderRadius: '14px', p: '3px',
                boxShadow: '0 6px 16px rgba(40,36,31,0.08), 0 1px 2px rgba(40,36,31,0.04)',
              }}>
                {visibleTabs.map(t => (
                  <Box
                    key={t.mode}
                    onClick={() => { setMode(t.mode); setTabPopOpen(false) }}
                    sx={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      borderRadius: '10px', cursor: 'pointer', px: '6px', fontSize: '13.5px', fontWeight: 600,
                      color: mode === t.mode ? 'primary.main' : 'text.primary',
                      bgcolor: mode === t.mode ? 'background.paper' : 'transparent',
                      boxShadow: mode === t.mode ? '0 1px 0 rgba(40,36,31,0.04)' : 'none',
                      '&:hover': mode === t.mode ? {} : { color: 'primary.dark' },
                    }}
                  >
                    {mode === t.mode ? t.activeIcon : t.icon}
                    {t.label}
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      ))}

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1 }}>
        {showExtensions || mode === 'chat' ? (
          <>
        {dests.map(d => (
          <Row
            key={d.path}
            icon={d.icon} label={d.label} badge={d.badge} tip={d.tip ?? d.label}
            active={isActive(d.path)}
            onClick={() => {
              if (d.path === '/chat') shell.newChat()
              else if (d.path === '/settings') shell.openSettings()  // Settings is an overlay, not a route
              else navigate(d.path)
            }}
          />
        ))}

        {/* Collapsed-only "All chats" affordance — the recent list can't render collapsed, so a single
            chat icon links to the full Chats page (the expanded mode uses the "All chats ›" link). */}
        {collapsed && (
          <Row
            icon={<ChatIcon fontSize="small" />} label="All chats" tip="All chats"
            active={location.pathname === '/chats'}
            onClick={() => navigate('/chats')}
          />
        )}

        {/* Pinned Extensions — above Recent chats (Cursor/Claude-style). Hidden when empty. */}
        {showExtensions && pinned.length > 0 && (
          <Box sx={{ mt: 2, animation: `${fadeIn} ${MOTION} ease both` }}>
            {!collapsed && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 2, mb: 0.5 }}>
                <PushPinOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 400, color: 'text.secondary' }}>
                  Pinned
                </Typography>
              </Box>
            )}
            {pinned.map((item) => {
              if (collapsed) {
                return (
                  <Row
                    key={item.id}
                    icon={pinnedIcon(item)}
                    label={item.name}
                    tip={`${item.name} (pinned)`}
                    onClick={() => openPinned(item)}
                  />
                )
              }
              return (
                <Box
                  key={item.id}
                  onClick={() => openPinned(item)}
                  sx={{
                    mx: 1, px: 1.25, py: 0.6, borderRadius: 1.5, cursor: 'pointer',
                    color: 'text.primary',
                    display: 'flex', alignItems: 'center', gap: 1, minWidth: 0,
                    '&:hover': { bgcolor: 'action.hover' },
                    '&:hover .unpin-btn': { opacity: 1 },
                  }}
                >
                  <Box sx={{ display: 'flex', color: 'text.secondary', flexShrink: 0 }}>
                    {pinnedIcon(item)}
                  </Box>
                  <Typography noWrap sx={{ flex: 1, fontSize: '14.5px', fontWeight: 500 }}>
                    {item.name}
                  </Typography>
                  <Tooltip title="Unpin" placement="right">
                    <IconButton
                      className="unpin-btn"
                      size="small"
                      aria-label={`Unpin ${item.name}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        unpinExtension(item.id)
                      }}
                      sx={{
                        opacity: 0, p: 0.35, color: 'text.secondary',
                        '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
                      }}
                    >
                      <PushPinOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )
            })}
          </Box>
        )}

        {!collapsed && recent.length > 0 && (
          <Box sx={{ mt: 2, animation: `${fadeIn} ${MOTION} ease both` }}>
            {/* Section header with the "All chats ›" link on the right (replaces the old bottom link). */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, mb: 0.5 }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 400, letterSpacing: '0', color: 'text.secondary' }}>Recent chats</Typography>
              <Box onClick={() => navigate('/chats')} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>All chats</Typography>
                <ChevronRightIcon sx={{ fontSize: 15 }} />
              </Box>
            </Box>
            {recent.map(s => {
              const active = shell.liveSessionId === s.id && location.pathname.startsWith('/chat')
              return (
                <RecentChatRow
                  key={s.id}
                  session={s}
                  active={active}
                  artifactCount={shell.artifactCountBySession[s.id]}
                  onOpen={() => shell.selectSession(s.id)}
                  onPin={pinned => void shell.setSessionPinned(s.id, pinned)}
                />
              )
            })}
          </Box>
        )}
          </>
        ) : (
          <>
            {catalogError && !collapsed && (
              <Box sx={{px: 2, py: 1.5}}>
                <Typography sx={{fontSize: '0.8rem', color: 'text.secondary'}}>Tools are temporarily unavailable.</Typography>
                <Typography onClick={() => setCatalogAttempt(value => value + 1)} sx={{fontSize: '0.78rem', color: 'primary.main', cursor: 'pointer', mt: 0.5}}>Retry</Typography>
              </Box>
            )}
            {flatRegistryEntries.map(e => {
              const tip = e.parentName ? `${e.name} (in ${e.parentName})` : e.name
              // Chosen icon (resolveIcon) with the per-kind icon as the fallback when none is set.
              const kindIcon = e.kind === 'public_app' ? <PublicIcon fontSize="small" /> : e.kind === 'tool' ? <BuildIcon fontSize="small" /> : <AppsOutlinedIcon fontSize="small" />
              return (
                <Row
                  key={e.id}
                  icon={resolveIcon(e.icon, kindIcon)}
                  label={e.name}
                  tip={tip}
                  onClick={() => openRegistryEntry(e)}
                />
              )
            })}
          </>
        )}
      </Box>

      {/* Collapsed New chat — pinned at the bottom of the rail, directly above the account block, so
          starting a new chat is always one click away when the rail is narrow. (Expanded mode keeps the
          + in the header next to the wordmark.) */}
      {collapsed && (
        <Tooltip title="New chat" placement="right">
          {/* Anchored left at 12px (= a 40px button centered in the 64px rail) rather than
              justifyContent:center — a centered button re-centers on every frame of the width animation
              and slides across; anchored, it simply appears in its final resting spot. */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', pl: '12px', pb: 1, animation: `${fadeIn} ${MOTION} ease both` }}>
            <IconButton
              size="small" onClick={() => shell.newChat()}
              sx={{ width: 40, height: 40, p: 0, borderRadius: '14px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', color: 'primary.main', boxShadow: '0 1px 0 rgba(40,36,31,0.04)', '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(0,143,168,0.10)' } }}
            >
              <AddIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Tooltip>
      )}

      {/* Account block — vertical padding is CONSTANT (py:1) so the avatar holds its Y across modes; only
          the horizontal padding tightens when collapsed. Bell sits inline with the account row when
          expanded; stacked above the avatar when collapsed (64px rail is too narrow for side-by-side). */}
      <Box sx={{
        borderTop: '1px solid', borderColor: 'divider', py: 1, px: collapsed ? 0.75 : 1.25,
        display: 'flex', flexDirection: collapsed ? 'column' : 'row', alignItems: 'center',
        gap: collapsed ? 0.25 : 0,
      }}>
        {showExtensions && collapsed && (
          <NotificationsMenu
            apiBaseUrl={shell.apiBaseUrl}
            token={shell.token}
            collapsed
            onAccepted={(result) => {
              navigate('/extensions', {
                state: {
                  extensionsTab: result.type === 'add_member' ? 'apps' : 'gadget',
                  extensionsRefresh: Date.now(),
                },
              })
            }}
          />
        )}
        <Box
          onClick={(e) => setAcctAnchor(e.currentTarget)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.25, cursor: 'pointer', borderRadius: 1.5, p: 0.5,
            flex: collapsed ? undefined : 1, minWidth: 0,
            justifyContent: collapsed ? 'center' : 'flex-start',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <ResolvedAvatar settings={shell.userSettings} fallbackName={shell.name || shell.email || undefined} size={30} />
          {!collapsed && (
            <Box sx={{ flex: 1, minWidth: 0, animation: `${fadeIn} ${MOTION} ease both` }}>
              <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{shell.name || shell.email || 'Account'}</Typography>
              <Typography noWrap sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>{ACTIVE_ORG}</Typography>
            </Box>
          )}
        </Box>
        {showExtensions && !collapsed && (
          <NotificationsMenu
            apiBaseUrl={shell.apiBaseUrl}
            token={shell.token}
            collapsed={false}
            onAccepted={(result) => {
              navigate('/extensions', {
                state: {
                  extensionsTab: result.type === 'add_member' ? 'apps' : 'gadget',
                  extensionsRefresh: Date.now(),
                },
              })
            }}
          />
        )}
        {/* Account menu opens UPWARD, left-aligned with the rail (like Claude) — it stays inside the
            sidebar column rather than jutting into the content area. */}
        <Menu
          anchorEl={acctAnchor} open={!!acctAnchor} onClose={() => setAcctAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }} transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          slotProps={{ paper: { sx: { minWidth: 248 } } }}
        >
          {/* Signed-in email */}
          <Typography sx={{ px: 2, pt: 1, pb: 0.5, fontSize: '0.72rem', color: 'text.secondary' }} noWrap>{shell.email || '—'}</Typography>

          {/* Org / Research Domain — one active row today; structured as a list so a user who belongs to
              multiple research domains can show several here later. */}
          <MenuItem disableRipple sx={{ cursor: 'default', '&:hover': { bgcolor: 'transparent' } }}>
            <ListItemIcon><CheckIcon fontSize="small" sx={{ color: 'primary.main' }} /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ sx: { fontSize: '0.85rem', fontWeight: 600 } }}>{ACTIVE_ORG}</ListItemText>
          </MenuItem>
          <Divider />

          <MenuItem onClick={() => { setAcctAnchor(null); shell.openSettings() }}>
            <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ sx: { fontSize: '0.85rem' } }}>Settings</ListItemText>
          </MenuItem>
          <MenuItem onClick={e => setLangAnchor(e.currentTarget)}>
            <ListItemIcon><LanguageOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ sx: { fontSize: '0.85rem' } }}>Language</ListItemText>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mr: 0.5 }}>English</Typography>
            <ChevronRightIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
          </MenuItem>
          <Divider />

          <MenuItem onClick={() => { setAcctAnchor(null); void shell.logout() }} sx={{ color: 'error.main' }}>
            <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ sx: { fontSize: '0.85rem' } }}>Log out</ListItemText>
          </MenuItem>
        </Menu>

        {/* Language submenu — English is selected; the rest preview locales we don't support yet
            (disabled), with a caption so the greyed list reads as deliberate, not broken. */}
        <Menu
          anchorEl={langAnchor} open={!!langAnchor} onClose={() => setLangAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{ paper: { sx: { minWidth: 230, maxHeight: 360 } } }}
        >
          {LANGUAGES.map((lang, i) => {
            const active = i === 0
            return (
              <MenuItem key={lang} disabled={!active} selected={active} onClick={() => setLangAnchor(null)}>
                <ListItemIcon>{active ? <CheckIcon fontSize="small" sx={{ color: 'primary.main' }} /> : null}</ListItemIcon>
                <ListItemText primaryTypographyProps={{ sx: { fontSize: '0.85rem' } }}>{lang}</ListItemText>
              </MenuItem>
            )
          })}
          <Divider />
          <Typography sx={{ px: 2, py: 0.75, fontSize: '0.72rem', color: 'text.disabled' }}>More languages coming soon</Typography>
        </Menu>
      </Box>
    </Box>
  )
}
